import * as Joi from "joi";
import { Moment } from 'moment';
import { Column, PrimaryGeneratedColumn } from 'typeorm';
import { booleanTransformer, GenPartial, momentTransformer } from 'typeormgen';
import { getInventoryComplexId } from './inventory.utils';
import { DEPARTMENTS, DISPOSITIONS, PURCHASE_TYPES } from '../enums/enums';
import EpisodicBase from '../episodic-base.entity';
import { Taggable } from '../taggable';
import WrapBox from '../wrap-boxes/wrap-box.entity';

export default class Inventory extends EpisodicBase implements Taggable {
	static deptId: DEPARTMENTS;
	static columns = [
		'id',
		'episodic_id',
		'name',
		'brand',
		'color',
		'description',
		'disposition',
		'disposition_notes',
		'size',
		'quantity',
		'quantity_notes',
		'asset_num',
		'unit_price',
		'total_cost',
		'purchase_type',
		'sku',
		'vendor_id',
		'category_id',
		'status_id',
		'storage_location_id',
		'asset_hub_exported',
		'last_updated_time',
		'last_updated_user',
		'created_by',
		'date_created',
		'hero',
		'start_date',
		'end_date',
	];

	static readonly skipUpdateColumns = [
		'id',
		'episodic_id',
		'created_by',
		'last_updated_user',
		'date_created',
		'last_updated_time',
	];

	static searchColumns = [
		'name',
		'brand',
		'color',
		'description',
		'disposition',
		'size',
		'quantity',
		'asset_num',
		'unit_price',
		'total_cost',
		'purchase_type',
		'sku',
		'start_date',
		'end_date',
		'last_updated_time',
		'date_created',
		'wrap_quantity',
		'quantity_notes',
		'material',
		'dimensions',
	];

	static sortColumns = [
		'name',
		'brand',
		'color',
		'description',
		'size',
		'asset_num',
		'unit_price',
		'total_cost',
		'purchase_type',
		'vendor_id',
		'sku',
		'start_date',
		'end_date',
		'disposition',
		'status_id',
		'last_updated_time',
		'date_created',
		'set_ids',
		'episodic_character_id',
		'category_id',
		'storage_location_id',
		'wrap_box_id',
	];

	static readonly selectableColumns = [
		'name',
		'brand',
		'color',
		'description',
		'quantity',
		'status_id',
		'is_personal',
		'episodic_character_id',
		'set_ids',
		'change_ids',
		'main_photo_id',
		'size',
		'category_id',
		'asset_num',
		'unit_price',
		'total_cost',
		'quantity_notes',
		'purchase_type',
		'vendor_id',
		'sku',
		'start_date',
		'end_date',
		'wrap_box_id',
		'storage_location_id',
		'disposition',
		'disposition_notes',
		'hero',
		'department',
		'episode',
		'asset_hub_exported',
	];

	static readonly foreignColumns = {
		EPISODIC_CHARACTER: 'episodic_character_id',
		STATUS: 'status_id',
		SETS: 'set_ids',
		CHANGES: 'change_ids',
		CATEGORY: 'category_id',
		VENDOR: 'vendor_id',
		WRAP_BOX: 'wrap_box_id',
		STORAGE_LOCATION: 'storage_location_id',
	};

	@PrimaryGeneratedColumn()
	id: number;

	@Column('int')
	episodic_id: number;

	@Column('varchar')
	name: string;

	@Column('varchar')
	brand: string;

	@Column('varchar')
	color: string;

	@Column('varchar')
	description: string;

	@Column('tinyint', { width: 1 })
	disposition: DISPOSITIONS;

	@Column('varchar')
	disposition_notes: string;

	@Column('varchar')
	size: string;

	@Column('int')
	quantity: number;

	@Column('varchar')
	quantity_notes: string;

	@Column('varchar')
	asset_num: string;

	@Column('decimal')
	unit_price: string;

	@Column('decimal')
	total_cost: string;

	@Column('int')
	purchase_type: PURCHASE_TYPES;

	@Column('varchar')
	sku: string;

	@Column('int')
	vendor_id: number;

	@Column('int')
	category_id: number;

	@Column('int')
	status_id: number;

	@Column('int')
	storage_location_id: number;

	@Column({ type: 'tinyint', transformer: booleanTransformer })
	asset_hub_exported: boolean;

	@Column({ type: 'timestamp', transformer: momentTransformer })
	last_updated_time: Moment;

	@Column('int')
	last_updated_user: number;

	@Column('int')
	created_by: number;

	@Column({ type: 'timestamp', transformer: momentTransformer })
	date_created: Moment;

	@Column({ type: 'tinyint', transformer: booleanTransformer })
	hero: boolean;

	@Column({ type: 'timestamp', transformer: momentTransformer })
	start_date: Moment;

	@Column({ type: 'timestamp', transformer: momentTransformer })
	end_date: Moment;

	department: DEPARTMENTS;
	main_photo_id: number;
	wrap_box_id: number;
	custom_sort_index: number;
	inv_wrap_box_names: string;
	storage_location_name: string;
	category_name: string;
	status_name: string;
	vendor_name: string;
	first_name: string;
	last_name: string;
	char_num: string;
	char_count: number;
	is_primary: boolean;

	private _complex_id: string;
	episodic_character_id: number;

	static parseComplexId(id: string): { department: DEPARTMENTS; id: number } {
		const results = id.match(/(CM|SET|PR)\.(\d+)/);
		if (results !== null) {
			return {
				department: DEPARTMENTS[results[1]],
				id: Number(results[2]),
			};
		}
		return null;
	}

	static parseComplexIds(ids: string[]) {
		const parsed = {
			[DEPARTMENTS.CM]: [] as number[],
			[DEPARTMENTS.PR]: [] as number[],
			[DEPARTMENTS.SET]: [] as number[],
		};

		if (ids && ids.length) {
			ids.forEach((id) => {
				const res = Inventory.parseComplexId(id);
				if (res) {
					parsed[res.department].push(res.id);
				}
			});
		}

		return parsed;
	}

	static rules() {
		return {
			...super.rules(),
			asset_hub_exported: Joi.boolean(),
			asset_num: Joi.string().max(300).allow(null, ''),
			brand: Joi.string().max(1000).allow(null, ''),
			category_id: Joi.number().integer().allow(null),
			color: Joi.string().max(500).allow(null, ''),
			created_by: Joi.number().integer(),
			date_created: Joi.date().allow(null),
			department: Joi.number().integer().required(),
			description: Joi.string().max(1000).allow(null, ''),
			disposition_notes: Joi.string().max(255).allow(null, ''),
			disposition: Joi.number().max(9).min(0).allow(null),
			end_date: Joi.date().allow(null),
			episodic_id: Joi.number().integer().required(),
			hero: Joi.boolean().allow(null),
			last_updated_time: Joi.date().allow(null),
			last_updated_user: Joi.number().integer().allow(null),
			main_photo_id: Joi.number().integer().allow(null),
			name: Joi.string().trim().invalid('').min(1).max(1000),
			purchase_type: Joi.number().integer().allow(null),
			quantity: Joi.number().integer().allow(null),
			quantity_notes: Joi.string().max(1000).allow(null, ''),
			size: Joi.string().max(300).allow(null, ''),
			sku: Joi.string().max(100).allow(null, ''),
			start_date: Joi.date().allow(null),
			status_id: Joi.number().integer().allow(null),
			storage_location_id: Joi.number().integer().allow(null),
			total_cost: Joi
				.string()
				.trim()
				.regex(/^\-?\d*\.?\d*$/)
				.allow(null, ''),
			unit_price: Joi
				.string()
				.trim()
				.regex(/^\-?\d*\.?\d*$/)
				.allow(null, ''),
			vendor_id: Joi.number().integer().allow(null),
			wrap_box_id: Joi.number().integer().allow(null),
			_complex_id: Joi.string().allow(null),
		};
	}

	toJSON() {
		return {
			...super.toJSON(),
			asset_hub_exported: this.asset_hub_exported,
			asset_num: this.asset_num,
			brand: this.brand,
			category_id: this.category_id,
			color: this.color,
			created_by: this.created_by,
			date_created: this.date_created,
			department: this.department,
			description: this.description,
			disposition_notes: this.disposition_notes,
			disposition: this.disposition,
			end_date: this.end_date,
			episodic_character_id: this.episodic_character_id,
			episodic_id: this.episodic_id,
			hero: this.hero,
			last_updated_time: this.last_updated_time,
			last_updated_user: this.last_updated_user,
			main_photo_id: this.main_photo_id,
			name: this.name,
			purchase_type: this.purchase_type,
			quantity: this.quantity,
			quantity_notes: this.quantity_notes,
			size: this.size,
			sku: this.sku,
			start_date: this.start_date,
			status_id: this.status_id,
			storage_location_id: this.storage_location_id,
			total_cost: this.total_cost,
			unit_price: this.unit_price,
			vendor_id: this.vendor_id,
		};
	}

	assign(props?: GenPartial<Inventory>) {
		super.assign(props);
		if (props) {
			if (typeof props.hero !== 'undefined') {
				this.hero = booleanTransformer.from(props.hero);
			}

			if (typeof props.asset_hub_exported !== 'undefined') {
				this.asset_hub_exported = booleanTransformer.from(
					props.asset_hub_exported
				);
			}

			if (typeof props.last_updated_time !== 'undefined') {
				this.last_updated_time = momentTransformer.from(
					props.last_updated_time
				);
			}

			if (typeof props.date_created !== 'undefined') {
				this.date_created = momentTransformer.from(props.date_created);
			}

			if (typeof props.start_date !== 'undefined') {
				this.start_date = momentTransformer.from(props.start_date);
			}

			if (typeof props.end_date !== 'undefined') {
				this.end_date = momentTransformer.from(props.end_date);
			}

			this._complex_id = getInventoryComplexId(this);
		}
	}

	constructor(props?: GenPartial<Inventory>) {
		super(props);

		this._complex_id = getInventoryComplexId(this);
	}

	getDisposition() {
		switch (this.disposition) {
			case DISPOSITIONS.OTHER:
				return 'Other';
			case DISPOSITIONS.APPROVED_SALE:
				return 'Approved Sale';
			case DISPOSITIONS.TRANSFER:
				return 'Transfer';
			case DISPOSITIONS.STORAGE:
				return 'Storage';
			case DISPOSITIONS.APPROVED_LOAN:
				return 'Approved Loan';
			case DISPOSITIONS.APPROVED_DONATION:
				return 'Approved Donation';
			case DISPOSITIONS.LOSS_DAMAGE:
				return 'Loss/Damage';
			case DISPOSITIONS.EXPENDABLES_CONSUMED:
				return 'Expendables/Consumed';
			case DISPOSITIONS.DESTROYED:
				return 'Destroyed/Demo/Strike';
			default:
				return '';
		}
	}
	getPurchaseType() {
		let purchaseType: string = PURCHASE_TYPES[this.purchase_type];
		return purchaseType ? purchaseType : '';
	}
	getWrapBoxNames(wrapBoxesArray: WrapBox[]) {
		let wrapBoxNames: string = '';
		wrapBoxesArray.forEach((cb) => {
			wrapBoxNames = wrapBoxNames.concat(cb.name).concat(',');
		});
		return wrapBoxNames.replace(/,\s*$/, '');
	}

	getComplexId(): string {
		return getInventoryComplexId(this);
	}

	get complexId(): string {
		return this._complex_id;
	}

	getInventoryTypeName(): string {
		if (this.department === DEPARTMENTS.PR) {
			return 'prop';
		} else if (this.department === DEPARTMENTS.SET) {
			return 'piece';
		}
		return 'item';
	}

	getTagName() {
		return this.name;
	}
}
